import {
  DateFormats,
  formatISODate,
  Icon,
  Inline,
  Stack,
  Table,
  type TableProps,
  Typography,
} from '@omnipresentgroup/design-system';
import { useBenefits } from '@omnipresentgroup/domains';
import type { Benefit, Benefits } from '@omnipresentgroup/types';
import {
  type ColumnDef,
  type ColumnOrderState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { type ReactElement, useMemo, useState } from 'react';

import { BenefitDetailsModal } from './components';
import { BenefitListColumnHeaders, useBenefitsTableConfig } from './hooks';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EMPTY_ARRAY: any[] = [];
const LOADING_ARRAY = Array.from({ length: 10 }, (_value, index) => ({
  id: index,
}));

const DEFAULT_SORT: SortingState = [
  {
    id: 'employeeName',
    desc: false,
  },
];

type Props = {
  benefitsInfo?: Benefits;
  isLoading?: boolean;
  isError?: boolean;
  columnVisibility: Partial<{
    [key in keyof typeof BenefitListColumnHeaders]: boolean;
  }>;
  columnOrderOverride?: (keyof typeof BenefitListColumnHeaders | string)[];
  optionsOverride?: Omit<TableOptions<Benefit>, 'data' | 'columns'>;
  columnsOverride?: ColumnDef<Benefit, unknown>[];
  emptyStateProps?: TableProps['emptyStateProps'];
  onSelectedRowsChange?: (selectedRowIds: string[]) => void;
  onRowClick?: (row: Benefit) => void;
  useRowSelection?: boolean;
  tableActions?: TableProps['tableActions'];
  showHeader?: boolean;
  filterSection?: ReactElement;
};

export const BenefitList = ({
  benefitsInfo,
  isLoading,
  isError,
  columnVisibility,
  columnOrderOverride,
  optionsOverride,
  columnsOverride,
  emptyStateProps,
  onRowClick,
  useRowSelection,
  tableActions,
  showHeader = true,
  filterSection,
}: Props) => {
  const {
    data: fetchedBenefitsInfo,
    isLoading: fetchedIsLoading,
    isError: fetchedIsError,
  } = useBenefits({
    enabled: !benefitsInfo, // Fetch only if benefitsInfo is not provided
  });

  const [modalOpen, setModalOpen] = useState<{
    open: boolean;
    benefit?: Benefit;
  }>({
    open: false,
  });

  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORT);

  const [columnVisibilityState, setColumnVisibilityState] = useState<
    Props['columnVisibility']
  >({
    ...Object.fromEntries(
      Object.keys(BenefitListColumnHeaders).map((current) => [current, false]),
    ),
    ...columnVisibility,
  });

  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
    columnOrderOverride || [],
  );

  const { columns } = useBenefitsTableConfig({
    columnsOverride,
  });

  const tableOptions: Omit<TableOptions<Benefit>, 'data' | 'columns'> = {
    state: {
      columnVisibility: columnVisibilityState,
      columnOrder,
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange: setColumnVisibilityState,
    onColumnOrderChange: setColumnOrder,
    getPaginationRowModel: getPaginationRowModel(),
    ...optionsOverride,
  };

  const dataTable = useMemo(() => {
    if (benefitsInfo) {
      return isLoading ? LOADING_ARRAY : (benefitsInfo.benefits ?? EMPTY_ARRAY);
    }
    return fetchedIsLoading
      ? LOADING_ARRAY
      : (fetchedBenefitsInfo?.benefits ?? EMPTY_ARRAY);
  }, [
    benefitsInfo,
    isLoading,
    fetchedBenefitsInfo?.benefits,
    fetchedIsLoading,
  ]);

  const table = useReactTable({
    data: dataTable,
    columns,
    ...tableOptions,
  });

  return (
    <>
      <Stack gap="8">
        <Table
          id="benefit-list"
          table={table}
          loading={isLoading || fetchedIsLoading}
          showErrorState={isError || fetchedIsError}
          useSorting
          usePagination
          onRowClick={(row: Benefit) => {
            if (!isLoading && !isError) {
              setModalOpen({ open: true, benefit: row });
            }
            onRowClick?.(row);
          }}
          emptyStateProps={emptyStateProps}
          useRowSelection={useRowSelection}
          tableActions={tableActions}
          showHeader={showHeader}
          filterSection={filterSection}
        />
        <Inline justify="space-between" align="center">
          <Inline align="center" gap="4">
            <Icon icon="CircleLetterM" size="16" />
            <Typography as="span" size="14">
              Indicates mandatory benefit
            </Typography>
          </Inline>
          {benefitsInfo?.lastUpdated && (
            <Typography as="span" size="14" color="subtle">
              <Typography as="span" size="14" weight="medium">
                Last update:{' '}
              </Typography>
              {formatISODate(benefitsInfo?.lastUpdated, DateFormats.Date)}
            </Typography>
          )}
        </Inline>
      </Stack>

      {modalOpen.open && modalOpen.benefit && (
        <BenefitDetailsModal
          modalProps={{
            open: modalOpen.open,
            onOpenChange: () =>
              setModalOpen({
                open: false,
              }),
          }}
          handleClose={() =>
            setModalOpen({
              open: false,
            })
          }
          benefit={modalOpen.benefit}
        />
      )}
    </>
  );
};
